import { History } from 'history';
import { PropsWithChildren, createContext, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Router, useHistory } from 'react-router';
import { Box, Flex } from 'reflexbox';

import { Modal, Popover } from '@treams/treams-ui';

export const ModalContext = createContext({ isModal: false, close: () => {} });

export function useModalLink(url: string, modal?: boolean) {
	const history = useHistory();

	if (modal === false) {
		return url;
	}
	const params = new URLSearchParams(history.location.search);
	params.set('modal', url);

	return history.location.pathname + '?' + params.toString();
}

function isGoalViewModal(url: string) {
	const path = url.split('?')[0];
	const reserved = new Set(['new', 'organization', 'collective', 'competencies']);
	const match = path.match(/^\/(?:users\/[^/]+\/)?goals\/([^/]+)$/);

	return !!match && !reserved.has(match[1]);
}

export default function ModalNavigation(props: PropsWithChildren<{ url: string }>) {
	const { t } = useTranslation();
	const history = useHistory();

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const listeners = useRef<Record<string, (location: any) => void>>({}).current;
	const containerRef = useRef<HTMLDivElement>(null);

	const visible = !!props.url;

	const lastURL = useRef(props.url);

	if (props.url) {
		lastURL.current = props.url;
	}

	const url = lastURL.current;
	const title = isGoalViewModal(url) ? t('goals.show.modal.title') : undefined;

	const [zoom, setZoom] = useState(false);

	const close = useCallback(() => {
		const location = history.location;
		const params = new URLSearchParams(location.search);
		params.delete('modal');
		location.search = params.toString();
		history.push(location);
	}, [history]);

	useEffect(() => {
		for (const listener of Object.values(listeners)) {
			listener(new URL('https://treams.com' + url));
		}

		setZoom(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.url]);

	const fake = useRef({
		...history,
		push(location: unknown) {
			if (location === null) {
				console.error('Location in ModalNavigation is null, this should not happen.');

				return history.push(
					history.createHref({
						...history.location,
						search: '',
					}),
				);
			}

			const isURL = typeof location === 'string';

			if (isURL) {
				return history.push(
					history.createHref({
						...history.location,
						search: `?modal=${location}`,
					}),
				);
			}

			const isLocationObject = typeof location === 'object';

			if (isLocationObject && 'pathname' in location) {
				return history.push(
					history.createHref({
						...history.location,
						search: `?modal=${location.pathname}`,
					}),
				);
			}
		},
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		listen(listener: any) {
			const id = Math.random();
			listeners[id] = listener;

			return () => {
				delete listeners[id];
			};
		},
		location: new URL('https://treams.com' + url),
	}).current as unknown as History;

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	(fake as any).location = new URL('https://treams.com' + url) as any;

	/**
	 * Scrolls the modal container to the top when the modal is opened.
	 * This is to prevent the modal from being opened with the user scrolled down,
	 * which can hide certain inputs.
	 */
	useEffect(() => {
		if (containerRef.current) {
			containerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
		}
	}, []);

	return (
		<Popover visible={visible} backdrop center zoom={zoom}>
			{lastURL.current && (
				<Flex flexDirection="column" width={800} maxHeight="calc(90vh)" maxWidth="calc(90vw)">
					<Modal.Header
						title={title}
						onFullScreen={() => {
							setZoom(true);
							history.push(props.url);
						}}
						onClose={close}
					/>
					<Box overflowY="scroll" ref={containerRef}>
						<Router key={props.url} history={fake}>
							<ModalContext.Provider value={{ isModal: true, close }}>
								{props.children}
							</ModalContext.Provider>
						</Router>
					</Box>
				</Flex>
			)}
		</Popover>
	);
}
